// client/src/pages/JobsPage.jsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Briefcase, Plus, X, ChevronRight, CalendarClock, List, Clock, AlertCircle } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useJobs, useAddJob, useUpdateJob, useVehicles } from '../api/hooks'

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  requested:         'bg-blue-500/15 text-blue-400',
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
  cancelled:         'bg-gray-500/15 text-gray-400',
}

const STATUS_LABELS = {
  requested:         'Requested',
  quoted:            'Quoted',
  awaiting_approval: 'Awaiting Approval',
  approved:          'Approved',
  in_progress:       'In Progress',
  waiting_on_parts:  'Waiting on Parts',
  completed:         'Completed',
  cancelled:         'Cancelled',
}

const STATUS_FILTERS = [
  { v: 'outstanding',        l: 'Outstanding'      },
  { v: 'requested',          l: 'Requested'        },
  { v: 'in_progress',        l: 'In Progress'      },
  { v: 'waiting_on_parts',   l: 'Waiting on Parts' },
  { v: 'awaiting_approval',  l: 'Awaiting'         },
  { v: 'approved',           l: 'Approved'         },
  { v: 'cancelled',          l: 'Cancelled'        },
]

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration }) {
  return (
    <span className="inline-block font-mono font-bold tracking-widest text-xs
                     bg-yellow-400 text-black px-2 py-0.5 rounded">
      {registration}
    </span>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return null
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Job row (list view) ──────────────────────────────────────────────────────

function JobRow({ job }) {
  const navigate = useNavigate()

  const scheduledLabel = job.scheduled_date
    ? `Scheduled ${fmtDate(job.scheduled_date)}${job.scheduled_time ? ` · ${job.scheduled_time.slice(0, 5)}` : ''}`
    : null

  const preferredLabel = !job.scheduled_date && job.preferred_date
    ? `Customer preferred ${fmtDate(job.preferred_date)}`
    : null

  return (
    <button
      onClick={() => navigate(`/garage/jobs/${job.id}`)}
      className="w-full flex items-center gap-4 px-5 py-4 bg-[var(--bg-surface)] border border-[var(--border)]
                 rounded-xl hover:border-[var(--border-strong)] transition-all text-left group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {job.registration && <Plate registration={job.registration} />}
          <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                         transition-colors truncate">
            {job.title}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-xs text-[var(--text-muted)]">
          {job.make && <span>{job.make} {job.model}</span>}
          {scheduledLabel && (
            <span className="flex items-center gap-1 text-indigo-400 font-medium">
              <CalendarClock size={11} />
              {scheduledLabel}
            </span>
          )}
          {preferredLabel && (
            <span className="flex items-center gap-1 text-amber-400">
              <CalendarClock size={11} />
              {preferredLabel}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={clsx('px-2 py-0.5 rounded text-xs font-medium',
          STATUS_STYLES[job.status] || 'bg-gray-500/15 text-gray-400')}>
          {STATUS_LABELS[job.status] || job.status}
        </span>
        <ChevronRight size={15} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
      </div>
    </button>
  )
}

// ─── Schedule view helpers ────────────────────────────────────────────────────

function groupJobs(jobs) {
  const now         = new Date()
  const todayStr    = now.toISOString().slice(0, 10)
  const tomorrow    = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowStr = tomorrow.toISOString().slice(0, 10)
  const weekEnd     = new Date(now); weekEnd.setDate(weekEnd.getDate() + 7)
  const weekEndStr  = weekEnd.toISOString().slice(0, 10)

  const groups = { unscheduled: [], pastDue: [], today: [], tomorrow: [], thisWeek: [], later: [] }

  for (const job of jobs) {
    if (['completed', 'cancelled'].includes(job.status)) continue
    if (!job.scheduled_date) { groups.unscheduled.push(job); continue }
    const d = job.scheduled_date.slice(0, 10)
    if      (d < todayStr)       groups.pastDue.push(job)
    else if (d === todayStr)     groups.today.push(job)
    else if (d === tomorrowStr)  groups.tomorrow.push(job)
    else if (d <= weekEndStr)    groups.thisWeek.push(job)
    else                         groups.later.push(job)
  }

  const byDate = (a, b) => {
    const da = a.scheduled_date + (a.scheduled_time || '00:00')
    const db = b.scheduled_date + (b.scheduled_time || '00:00')
    return da.localeCompare(db)
  }
  groups.pastDue.sort(byDate)
  groups.today.sort(byDate)
  groups.tomorrow.sort(byDate)
  groups.thisWeek.sort(byDate)
  groups.later.sort(byDate)

  return groups
}

// ─── Schedule job card ────────────────────────────────────────────────────────

function ScheduleJobCard({ job }) {
  const navigate  = useNavigate()
  const updateJob = useUpdateJob()
  const [editing, setEditing] = useState(false)
  const [newDate, setNewDate] = useState(job.scheduled_date?.slice(0, 10) || '')
  const [newTime, setNewTime] = useState(job.scheduled_time?.slice(0, 5)  || '')

  const handleSave = async () => {
    try {
      await updateJob.mutateAsync({
        id:            job.id,
        scheduledDate: newDate || null,
        scheduledTime: newTime || null,
      })
      toast.success('Schedule updated')
      setEditing(false)
    } catch {
      toast.error('Failed to update schedule')
    }
  }

  const fmtTime = () => {
    if (!job.scheduled_date) return null
    const d = new Date(job.scheduled_date).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short',
    })
    return job.scheduled_time ? `${d} · ${job.scheduled_time.slice(0, 5)}` : d
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {job.registration && <Plate registration={job.registration} />}
            <button
              onClick={() => navigate(`/garage/jobs/${job.id}`)}
              className="text-sm font-medium text-[var(--text-primary)] hover:text-indigo-400
                         transition-colors text-left"
            >
              {job.title}
            </button>
          </div>

          {job.make && (
            <p className="text-xs text-[var(--text-muted)] mb-1.5">{job.make} {job.model}</p>
          )}

          {fmtTime() && !editing && (
            <div className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] mb-1.5">
              <Clock size={11} />
              {fmtTime()}
            </div>
          )}

          {editing ? (
            <div className="mt-2 flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-xs text-[var(--text-muted)] mb-1">Date</label>
                <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                  className="px-2 py-1.5 text-xs bg-[var(--bg-base)] border border-[var(--border)]
                             rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-xs text-[var(--text-muted)] mb-1">Time</label>
                <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)}
                  className="px-2 py-1.5 text-xs bg-[var(--bg-base)] border border-[var(--border)]
                             rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-indigo-500" />
              </div>
              <div className="flex gap-1.5">
                <button onClick={handleSave} disabled={updateJob.isPending}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                             text-white text-xs font-medium rounded-lg transition-colors">
                  Save
                </button>
                <button onClick={() => setEditing(false)}
                  className="px-3 py-1.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                             text-[var(--text-secondary)] text-xs rounded-lg transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEditing(true)}
              className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">
              {job.scheduled_date ? 'Reschedule' : 'Schedule'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className={clsx('px-2 py-0.5 rounded text-xs font-medium',
            STATUS_STYLES[job.status] || 'bg-gray-500/15 text-gray-400')}>
            {STATUS_LABELS[job.status] || job.status}
          </span>
          <button onClick={() => navigate(`/garage/jobs/${job.id}`)}
            className="p-1 text-[var(--text-muted)] hover:text-indigo-400 transition-colors">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Schedule group ───────────────────────────────────────────────────────────

function ScheduleGroup({ title, jobs, accent, alert }) {
  if (jobs.length === 0) return null
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {alert && <AlertCircle size={16} className={clsx('shrink-0', accent)} />}
        <h2 className={clsx('font-display text-base font-bold', accent || 'text-[var(--text-primary)]')}>
          {title}
        </h2>
        <span className={clsx(
          'px-2 py-0.5 rounded-full text-xs font-medium',
          accent === 'text-red-400'    ? 'bg-red-500/15 text-red-400'    :
          accent === 'text-amber-400'  ? 'bg-amber-500/15 text-amber-400' :
          accent === 'text-green-400'  ? 'bg-green-500/15 text-green-400' :
          'bg-[var(--bg-overlay)] text-[var(--text-muted)]'
        )}>
          {jobs.length}
        </span>
      </div>
      <div className="space-y-2">
        {jobs.map(j => <ScheduleJobCard key={j.id} job={j} />)}
      </div>
    </div>
  )
}

// ─── Create Job modal ─────────────────────────────────────────────────────────

function CreateJobModal({ onClose }) {
  const addJob = useAddJob()
  const { data: vehicles = [] } = useVehicles()
  const [form, setForm] = useState({
    vehicleId: '', title: '', description: '',
    status: 'in_progress', dateBooked: '',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await addJob.mutateAsync({
        vehicleId:   form.vehicleId,
        title:       form.title,
        description: form.description || null,
        status:      form.status,
        dateBooked:  form.dateBooked  || null,
      })
      toast.success('Job created')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create job')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">New Job</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Vehicle</label>
            <select value={form.vehicleId} onChange={set('vehicleId')} className={inputCls} required>
              <option value="">Select a vehicle…</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>
                  {v.registration} — {v.make} {v.model}
                  {v.owner_name ? ` (${v.owner_name})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Job title</label>
            <input type="text" value={form.title} onChange={set('title')}
              placeholder="e.g. Full service and MOT" className={inputCls} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
            <textarea rows={2} value={form.description} onChange={set('description')}
              placeholder="Details of work required…" className={clsx(inputCls, 'resize-none')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Status</label>
              <select value={form.status} onChange={set('status')} className={inputCls}>
                <option value="in_progress">In Progress</option>
                <option value="awaiting_approval">Awaiting Approval</option>
                <option value="approved">Approved</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Date booked</label>
              <input type="date" value={form.dateBooked} onChange={set('dateBooked')} className={inputCls} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addJob.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addJob.isPending ? 'Creating…' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function JobsPage() {
  const { user } = useAuth()
  const [viewMode,      setViewMode]      = useState('list')
  const [statusFilter,  setStatusFilter]  = useState('outstanding')
  const [vehicleFilter, setVehicleFilter] = useState('')
  const [showCreate,    setShowCreate]    = useState(false)

  const isGarageAdmin = ['garage_admin', 'platform_admin'].includes(user?.role)
  const isGarage      = ['garage_user', 'garage_admin', 'platform_admin'].includes(user?.role)

  const { data: vehicles = [] } = useVehicles()

  // For 'outstanding' we fetch all and filter client-side.
  // For a specific status we pass it to the API.
  const { data: allJobs = [], isLoading } = useJobs({
    ...(statusFilter && statusFilter !== 'outstanding' && viewMode === 'list'
      ? { status: statusFilter }
      : {}),
    ...(vehicleFilter ? { vehicleId: vehicleFilter } : {}),
  })

  const jobs = statusFilter === 'outstanding'
    ? allJobs.filter(j => !['completed', 'cancelled'].includes(j.status))
    : allJobs

  // Schedule view always uses all non-completed jobs regardless of list filter
  const groups     = groupJobs(allJobs)
  const totalSched = Object.values(groups).reduce((s, g) => s + g.length, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Jobs</h1>
          {!isLoading && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {viewMode === 'schedule'
                ? `${totalSched} active ${totalSched === 1 ? 'job' : 'jobs'}`
                : statusFilter === 'outstanding'
                  ? `${jobs.length} outstanding ${jobs.length === 1 ? 'job' : 'jobs'}`
                  : `${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* View toggle */}
          <div className="flex items-center gap-0.5 p-0.5 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              title="List view"
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}
            >
              <List size={13} /> List
            </button>
            <button
              onClick={() => setViewMode('schedule')}
              title="Schedule view"
              className={clsx(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors',
                viewMode === 'schedule'
                  ? 'bg-indigo-600 text-white'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              )}
            >
              <CalendarClock size={13} /> Schedule
            </button>
          </div>

          {isGarage && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Plus size={16} />
              New Job
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        {/* Vehicle filter (garage only) */}
        {isGarage && vehicles.length > 0 && (
          <select
            value={vehicleFilter}
            onChange={e => setVehicleFilter(e.target.value)}
            className="w-full sm:w-auto px-3 py-1.5 text-sm bg-[var(--bg-surface)] border border-[var(--border)]
                       rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-indigo-500 transition-colors"
          >
            <option value="">All vehicles</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>
                {v.registration} — {v.make} {v.model}
                {v.owner_name ? ` (${v.owner_name})` : ''}
              </option>
            ))}
          </select>
        )}

        {/* Status filters (list mode only) */}
        {viewMode === 'list' && (
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_FILTERS.map(f => (
              <button key={f.v} onClick={() => setStatusFilter(f.v)}
                className={clsx('px-3 py-1 rounded-full text-xs font-medium transition-colors',
                  statusFilter === f.v
                    ? 'bg-indigo-600 text-white'
                    : 'bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                )}>
                {f.l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : viewMode === 'list' ? (
        jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)]
                            flex items-center justify-center mb-4">
              <Briefcase size={28} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No jobs</h3>
            <p className="text-sm text-[var(--text-secondary)] max-w-xs">
              {vehicleFilter && statusFilter === 'outstanding'
                ? 'No outstanding jobs for this vehicle.'
                : statusFilter === 'outstanding'
                  ? isGarage
                    ? 'No outstanding jobs. Completed jobs move to the vehicle history.'
                    : 'No outstanding jobs for your vehicles.'
                  : 'No jobs match the current filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map(j => <JobRow key={j.id} job={j} />)}
          </div>
        )
      ) : (
        /* Schedule view */
        totalSched === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)]
                            flex items-center justify-center mb-4">
              <CalendarClock size={28} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No active jobs</h3>
            <p className="text-sm text-[var(--text-secondary)] max-w-xs">
              {vehicleFilter
                ? 'No active jobs for this vehicle.'
                : 'Active jobs will appear here. Set a scheduled date on any job to organise the diary.'}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {groups.pastDue.length > 0 && (
              <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle size={16} className="text-red-400 shrink-0" />
                  <h2 className="font-display text-base font-bold text-red-400">Past Due</h2>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400">
                    {groups.pastDue.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {groups.pastDue.map(j => <ScheduleJobCard key={j.id} job={j} />)}
                </div>
              </div>
            )}
            <ScheduleGroup title="Today"       jobs={groups.today}       accent="text-green-400" />
            <ScheduleGroup title="Tomorrow"    jobs={groups.tomorrow}    accent="text-indigo-400" />
            <ScheduleGroup title="This Week"   jobs={groups.thisWeek} />
            <ScheduleGroup title="Later"       jobs={groups.later} />
            <ScheduleGroup title="Unscheduled" jobs={groups.unscheduled} accent="text-amber-400" />
          </div>
        )
      )}

      {showCreate && <CreateJobModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
